import { t } from '@/features/i18n/utils';
import { ProcurementBreadcrumbs } from '@/features/subcontractor-bids/components/procurement-breadcrumbs';
import { Suspense } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Spinner } from '@/ui/components/spinner';
import { useAtom } from 'jotai';
import { Icon } from '@/ui/components/icon';
import { FolderIcon } from '@hugeicons-pro/core-stroke-rounded';
import { BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderChatButton } from '@/ui/components/header';
import { MainHeader, MainHeaderBackButton, MainScrollArea } from '@/ui/components/main';
import { H1 } from '@/ui/components/heading';
import { Tabs, TabsList, TabsTab } from '@/ui/components/tabs';
import { FilesPageList } from '@/features/documents/components/files-page/files-page-list';
import {
  FilesPageAddFilesButton,
  FilesPageCount,
} from '@/features/documents/components/files-page/files-page-header';
import { FileUploadDialog } from '@/features/documents/components/file-upload';
import { IndexingStatusAlerts } from '@/features/documents/components/indexing-status-alerts';
import { offersQuery } from '@/features/subcontractor-bids/queries';
import { filesSupplierAtom } from '@/features/subcontractor-bids/store';
import { CompanyAvatar } from '@/features/companies/components/company-avatar';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId/files/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId, workspaceId, procurementId } = Route.useParams();
  const navigate = useNavigate();

  const { data: offers } = useSuspenseQuery(offersQuery(workspaceId, procurementId));
  const [supplier, setSupplier] = useAtom(filesSupplierAtom);
  const subcontractorId =
    offers.find((offer) => offer.id === supplier)?.id ?? offers[0]?.id ?? null;

  return (
    <>
      <Header>
        <HeaderBackButton />
        <ProcurementBreadcrumbs>
          <BreadcrumbPage>
            <Icon icon={FolderIcon} />
            {t({ en: 'Files', nb: 'Filer' })}
          </BreadcrumbPage>
        </ProcurementBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <MainScrollArea>
        <MainHeader>
          <H1>
            <MainHeaderBackButton
              to="/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId"
              params={{ projectId, workspaceId, procurementId }}
            />
            {t({ en: 'Files', nb: 'Filer' })}
            {subcontractorId && (
              <FilesPageCount resourceId={subcontractorId} resourceType="subcontractor" />
            )}
          </H1>
          {subcontractorId && (
            <FilesPageAddFilesButton resourceId={subcontractorId} resourceType="subcontractor" />
          )}
        </MainHeader>
        <Tabs value={subcontractorId ?? ''} onValueChange={(v) => setSupplier(v as string)}>
          <TabsList className="px-x" containerClassName="has-[+div:empty]:border-b-0">
            {offers.map((offer) => (
              <TabsTab key={offer.id} value={offer.id}>
                <CompanyAvatar company={{ id: offer.id, name: offer.companyName }} size={20} />
                {offer.companyName}
              </TabsTab>
            ))}
          </TabsList>
          {subcontractorId && (
            <div className="not-empty:pb-3">
              <IndexingStatusAlerts
                resourceId={subcontractorId}
                documentType="subcontractor"
                waitForKg
              />
            </div>
          )}
        </Tabs>
        {subcontractorId && (
          <div key={subcontractorId} className="contents">
            <Suspense fallback={<Spinner kind="inset" />}>
              <FilesPageList
                resourceId={subcontractorId}
                resourceType="subcontractor"
                onRowClick={(doc) => {
                  navigate({
                    to: '/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId/files/$documentId',
                    params: {
                      projectId,
                      workspaceId,
                      procurementId,
                      documentId: doc.id,
                    },
                  });
                }}
              />
            </Suspense>
            <FileUploadDialog resourceId={subcontractorId} resourceType="subcontractor" />
          </div>
        )}
      </MainScrollArea>
    </>
  );
}
